import React, { useEffect, useMemo, useState } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../../lib/firebase";
import DuplicateMatchPanel from "./DuplicateMatchPanel";
import {
  buildFullName,
  buildFullNameLower,
  calculateAgeFromDob,
  findDuplicateMatches,
  normalizePhone,
  normalizeSpaces,
  toTitleCase,
} from "../../utils/patient/patientUtils";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  zIndex: 2000,
};

const modalStyle = {
  width: "100%",
  maxWidth: 980,
  maxHeight: "90vh",
  overflowY: "auto",
  borderRadius: 18,
  background: "#f8fafc",
  boxShadow: "0 20px 50px rgba(15, 23, 42, 0.2)",
  border: "1px solid #e5e7eb",
};

const sectionStyle = {
  padding: 18,
  borderBottom: "1px solid #e5e7eb",
};

const fieldGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 12,
};

const labelStyle = {
  display: "grid",
  gap: 6,
  fontSize: 14,
  color: "#334155",
};

const inputStyle = {
  width: "100%",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "10px 12px",
  fontSize: 14,
  background: "#fff",
  boxSizing: "border-box",
};

const textareaStyle = {
  ...inputStyle,
  minHeight: 88,
  resize: "vertical",
};

const buttonStyle = {
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "10px 14px",
  background: "#fff",
  cursor: "pointer",
};

const primaryButtonStyle = {
  ...buttonStyle,
  background: "#0f172a",
  color: "#fff",
  borderColor: "#0f172a",
};

const checkboxRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginTop: 8,
  color: "#334155",
  fontSize: 14,
};

const initialForm = {
  firstName: "",
  lastName: "",
  dob: "",
  age: "",
  gender: "",
  phone: "",
  email: "",
  streetAddress: "",
  city: "",
  state: "",
  zip: "",
  allergies: "",
  allergiesNone: false,
  medications: "",
  medicationsNone: false,
  notes: "",
};

function digitsOnly(value = "") {
  return String(value || "").replace(/\D/g, "");
}

function formatPhoneInput(value = "") {
  const digits = digitsOnly(value).slice(0, 10);
  if (!digits) return "";
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function formatState(value = "") {
  return String(value || "").replace(/[^a-z]/gi, "").toUpperCase().slice(0, 2);
}

function formatZip(value = "") {
  return String(value || "").replace(/\D/g, "").slice(0, 5);
}

function buildAddressString(form) {
  const street = normalizeSpaces(form.streetAddress);
  const city = normalizeSpaces(form.city);
  const state = normalizeSpaces(form.state);
  const zip = normalizeSpaces(form.zip);

  const cityState = [city, state].filter(Boolean).join(", ");
  const line2 = [cityState, zip].filter(Boolean).join(" ");
  return [street, line2].filter(Boolean).join(", ");
}

export default function NewPatientModal({
  isOpen,
  onClose,
  patients = [],
  onPatientCreated,
  onOpenExistingPatient,
}) {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setForm(initialForm);
      setSaving(false);
      setError("");
    }
  }, [isOpen]);

  const matches = useMemo(() => findDuplicateMatches(patients, form), [patients, form]);

  function setField(field, value) {
    setForm((prev) => {
      const next = { ...prev, [field]: value };

      if (field === "dob") {
        next.age = calculateAgeFromDob(value);
      }

      if (field === "phone") {
        next.phone = formatPhoneInput(value);
      }

      if (field === "state") {
        next.state = formatState(value);
      }

      if (field === "zip") {
        next.zip = formatZip(value);
      }

      return next;
    });
  }

  function setAllergiesNone(checked) {
    setForm((prev) => ({
      ...prev,
      allergiesNone: checked,
      allergies: checked ? "" : prev.allergies,
    }));
  }

  function setMedicationsNone(checked) {
    setForm((prev) => ({
      ...prev,
      medicationsNone: checked,
      medications: checked ? "" : prev.medications,
    }));
  }

  async function handleCreatePatient() {
    setError("");

    const firstName = toTitleCase(form.firstName);
    const lastName = toTitleCase(form.lastName);

    if (!firstName || !lastName) {
      setError("First name and last name are required.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        firstName,
        lastName,
        fullName: buildFullName(firstName, lastName),
        fullNameLower: buildFullNameLower(firstName, lastName),

        dob: normalizeSpaces(form.dob),
        age: normalizeSpaces(form.age),
        gender: normalizeSpaces(form.gender),

        phone: normalizeSpaces(form.phone),
        phoneDigitsOnly: normalizePhone(form.phone),
        email: normalizeSpaces(form.email),

        streetAddress: normalizeSpaces(form.streetAddress),
        city: normalizeSpaces(form.city),
        state: normalizeSpaces(form.state),
        zip: normalizeSpaces(form.zip),
        address: buildAddressString(form),

        allergies: form.allergiesNone ? "" : normalizeSpaces(form.allergies),
        allergiesNone: Boolean(form.allergiesNone),

        medications: form.medicationsNone ? "" : normalizeSpaces(form.medications),
        medicationsNone: Boolean(form.medicationsNone),

        lastEncounterAt: null,
        encounterCount: 0,
        notes: normalizeSpaces(form.notes),

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdByUid: "",
        createdByName: "",
        updatedByUid: "",
        updatedByName: "",
      };

      const ref = await addDoc(collection(db, "patients"), payload);

      onPatientCreated?.({
        id: ref.id,
        ...payload,
      });

      onClose?.();
    } catch (err) {
      console.error("Error creating patient:", err);
      setError("Could not create patient. Check the console for details.");
    } finally {
      setSaving(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ ...sectionStyle, display: "flex", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 20 }}>New Patient</div>
            <div style={{ color: "#64748b", marginTop: 4 }}>
              Start with the basics. If this person already exists, use the match panel instead of creating a duplicate.
            </div>
          </div>

          <button type="button" style={buttonStyle} onClick={onClose}>
            Close
          </button>
        </div>

        <div style={sectionStyle}>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Quick identity check</div>
          <div style={fieldGridStyle}>
            <label style={labelStyle}>
              First Name *
              <input
                style={inputStyle}
                value={form.firstName}
                onChange={(e) => setField("firstName", e.target.value)}
              />
            </label>

            <label style={labelStyle}>
              Last Name *
              <input
                style={inputStyle}
                value={form.lastName}
                onChange={(e) => setField("lastName", e.target.value)}
              />
            </label>

            <label style={labelStyle}>
              DOB
              <input
                style={inputStyle}
                type="date"
                value={form.dob}
                onChange={(e) => setField("dob", e.target.value)}
              />
            </label>

            <label style={labelStyle}>
              Phone
              <input
                style={inputStyle}
                inputMode="tel"
                placeholder="(555) 555-5555"
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
              />
            </label>
          </div>

          <DuplicateMatchPanel
            matches={matches}
            onOpenPatient={(patient) => {
              onOpenExistingPatient?.(patient, { createEncounter: false });
              onClose?.();
            }}
            onUseExistingPatient={(patient) => {
              onOpenExistingPatient?.(patient, { createEncounter: true });
              onClose?.();
            }}
          />
        </div>

        <div style={sectionStyle}>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Patient details</div>

          <div style={fieldGridStyle}>
            <label style={labelStyle}>
              Age
              <input
                style={inputStyle}
                value={form.age}
                onChange={(e) => setField("age", e.target.value)}
              />
            </label>

            <label style={labelStyle}>
              Gender
              <select
                style={inputStyle}
                value={form.gender}
                onChange={(e) => setField("gender", e.target.value)}
              >
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </label>

            <label style={labelStyle}>
              Email
              <input
                style={inputStyle}
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
              />
            </label>
          </div>

          <div style={{ ...fieldGridStyle, marginTop: 12 }}>
            <label style={labelStyle}>
              Street Address
              <input
                style={inputStyle}
                value={form.streetAddress}
                onChange={(e) => setField("streetAddress", e.target.value)}
              />
            </label>

            <label style={labelStyle}>
              City
              <input
                style={inputStyle}
                value={form.city}
                onChange={(e) => setField("city", e.target.value)}
              />
            </label>

            <label style={labelStyle}>
              State
              <input
                style={inputStyle}
                placeholder="CT"
                value={form.state}
                onChange={(e) => setField("state", e.target.value)}
              />
            </label>

            <label style={labelStyle}>
              Zip
              <input
                style={inputStyle}
                inputMode="numeric"
                placeholder="06095"
                value={form.zip}
                onChange={(e) => setField("zip", e.target.value)}
              />
            </label>
          </div>

          <div style={{ ...fieldGridStyle, marginTop: 12 }}>
            <div>
              <label style={labelStyle}>
                Allergies
                <textarea
                  style={{
                    ...textareaStyle,
                    background: form.allergiesNone ? "#f8fafc" : "#fff",
                    color: form.allergiesNone ? "#94a3b8" : "#0f172a",
                  }}
                  value={form.allergiesNone ? "" : form.allergies}
                  disabled={form.allergiesNone}
                  onChange={(e) => setField("allergies", e.target.value)}
                />
              </label>

              <label style={checkboxRowStyle}>
                <input
                  type="checkbox"
                  checked={form.allergiesNone}
                  onChange={(e) => setAllergiesNone(e.target.checked)}
                />
                None
              </label>
            </div>

            <div>
              <label style={labelStyle}>
                Medications
                <textarea
                  style={{
                    ...textareaStyle,
                    background: form.medicationsNone ? "#f8fafc" : "#fff",
                    color: form.medicationsNone ? "#94a3b8" : "#0f172a",
                  }}
                  value={form.medicationsNone ? "" : form.medications}
                  disabled={form.medicationsNone}
                  onChange={(e) => setField("medications", e.target.value)}
                />
              </label>

              <label style={checkboxRowStyle}>
                <input
                  type="checkbox"
                  checked={form.medicationsNone}
                  onChange={(e) => setMedicationsNone(e.target.checked)}
                />
                None
              </label>
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            <label style={labelStyle}>
              Notes
              <textarea
                style={textareaStyle}
                value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
              />
            </label>
          </div>
        </div>

        <div
          style={{
            ...sectionStyle,
            borderBottom: "none",
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
          }}
        >
          <div style={{ color: error ? "#b91c1c" : "#64748b" }}>
            {error || "Creating a patient here does not touch the rest of the app."}
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" style={buttonStyle} onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button
              type="button"
              style={primaryButtonStyle}
              onClick={handleCreatePatient}
              disabled={saving}
            >
              {saving ? "Saving..." : "Create Patient"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}